import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { isNotNil } from 'ramda'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import { Course, Event } from 'src/v3/core/types'

// ─── Local types ─────────────────────────────────────────────────────────────

type CourseWithSection = Course & {
  sectionName: string
}

// ─── Exported types ───────────────────────────────────────────────────────────

export type EventSentimentAnalysis = {
  id: string
  correlationID: string
  eventID: string
  licenseGroup: string // License group (e.g., 'Paramedic', 'EMT', 'AEMT', 'Nurse', 'Other', 'All')
  startDate: string
  endDate: string
  name?: string
  status: string
  createdTime: string
  updatedTime: string
  result?: string // Markdown result from polling (added by frontend)
}

export type CourseSentimentAnalysis = {
  id: string
  correlationID: string
  courseID: string
  licenseGroup: string // License group (e.g., 'Paramedic', 'EMT', 'AEMT', 'Nurse', 'Other', 'All')
  startDate: string
  endDate: string
  name?: string
  status: string
  createdTime: string
  updatedTime: string
  result?: string // Markdown result from polling (added by frontend)
}

export type SentimentAnalysisRequest = {
  eventID: string
  licenseGroup: string
  startDate: string
  endDate: string
  prompt: string
  name?: string
}

export type CourseSentimentAnalysisRequest = {
  courseID: string
  licenseGroup: string
  startDate: string
  endDate: string
  prompt: string
  name?: string
}

export type EventLikertAnalysis = {
  id: string
  correlationID: string
  eventID: string
  licenseGroup: string
  startDate: string
  endDate: string
  name?: string | null
  status: string
  createdTime: string
  updatedTime: string
  result?: string
}

export type CourseLikertAnalysis = {
  id: string
  correlationID: string
  courseID: string
  licenseGroup: string
  startDate: string
  endDate: string
  name?: string | null
  status: string
  createdTime: string
  updatedTime: string
  result?: string
}

export type LikertAnalysisRequest = {
  eventID: string
  licenseGroup: string
  startDate: string
  endDate: string
  name?: string | null
}

export type CourseLikertAnalysisRequest = {
  courseID: string
  licenseGroup: string
  startDate: string
  endDate: string
  name?: string | null
}

export type JobProgress = {
  timestamp: string
  phase: string
  message: string
}

export type JobResult = {
  correlationID: string
  response: string
  timestamp: string
}

export type JobStatusResponse = {
  correlationID: string
  status: 'SessionInProgress' | 'SessionCompleted' | 'SessionFailed'
  createdAt: string
  updatedAt: string
  progress: JobProgress[]
  result: JobResult | null
}

export type LicenseGroupBreakdown = {
  nurse: number
  paramedic: number
  emt: number
  aemt: number
  other: number
  total: number
}

export type LearnerSnapshotWeek = {
  weekStart: string
  isoWeek: string
  enrollments: LicenseGroupBreakdown
  completions: LicenseGroupBreakdown
  quizAttempts: number
  quizPasses: number
  firstAttemptPasses: number
}

export type LearnerSnapshotYear = {
  year: number
  completions: LicenseGroupBreakdown
  enrollments: LicenseGroupBreakdown
  quizAttempts: number
  quizPasses: number
  quizPassRatePct: number
  firstAttemptPassRatePct: number
  avgQuizScore: number
}

export type QuizScoreBucket = {
  range: string
  count: number
}

export type LearnerSnapshot = {
  eventID: string
  generatedAt: string
  windowStart: string
  totals: {
    totalCompletions: number
    totalShowedUp: number
    completionRatePct: number
    quizPassRatePct: number
    firstAttemptPassRatePct: number
    avgQuizScore: number
    activeLast30Days: number
    completionsByLicenseGroup: LicenseGroupBreakdown
  }
  byYear: LearnerSnapshotYear[]
  series: LearnerSnapshotWeek[]
  quizScoreDistribution: QuizScoreBucket[]
}

export type NurseGrowthYear = {
  year: number
  uniqueUsers: number
}

export type NurseGrowthMonth = {
  year: number
  month: number
  uniqueUsers: number
}

export type NurseGrowthWeek = {
  weekStart: string
  isoWeek: string
  uniqueUsers: number
}

export type NurseGrowth = {
  generatedAt: string
  byYear: NurseGrowthYear[]
  byMonth: NurseGrowthMonth[]
  byWeek: NurseGrowthWeek[]
}

export type LearnerSnapshotDomain = 'live' | 'enduring'

export type LearnerSnapshotArgs = {
  itemID: string
  domain: LearnerSnapshotDomain
}

// ─── Cache key helpers ────────────────────────────────────────────────────────

let learnerSnapshotCacheKey = (args: LearnerSnapshotArgs) =>
  `${args.domain}:${args.itemID}`

let learnerSnapshotUrl = (args: LearnerSnapshotArgs) =>
  args.domain === 'live'
    ? `/api/content-pipeline/performance-event/learner-snapshot/${args.itemID}`
    : `/api/content-pipeline/performance-course/learner-snapshot/${args.itemID}`

// ─── Thunks ───────────────────────────────────────────────────────────────────

export let fetchNurseGrowth = createAsyncThunk(
  'performance/fetchNurseGrowth',
  () =>
    axios
      .get<NurseGrowth>(
        '/api/content-pipeline/performance-platform/nurse-growth',
      )
      .then(pluckData),
)

export let fetchLearnerSnapshot = createAsyncThunk(
  'performance/fetchLearnerSnapshot',
  (args: LearnerSnapshotArgs) =>
    axios.get<LearnerSnapshot>(learnerSnapshotUrl(args)).then(pluckData),
)

export let fetchEventSentimentAnalyses = createAsyncThunk(
  'performance/fetchEventSentimentAnalyses',
  (eventID: string) =>
    axios
      .get<EventSentimentAnalysis[]>(
        `/api/content-pipeline/performance-event/sentiment-analysis/event/${eventID}`,
      )
      .then(pluckData),
)

export let createSentimentAnalysis = createAsyncThunk(
  'performance/createSentimentAnalysis',
  (request: SentimentAnalysisRequest) =>
    axios
      .post<{
        correlationID: string
      }>('/api/content-pipeline/performance-event/sentiment-analysis', request)
      .then(pluckData),
)

export let pollSentimentAnalysisStatus = createAsyncThunk(
  'performance/pollSentimentAnalysisStatus',
  (correlationID: string) =>
    axios
      .get<JobStatusResponse>(
        `/api/content-pipeline/performance-event/sentiment-analysis/${correlationID}`,
      )
      .then(pluckData),
)

export let fetchCourseSentimentAnalyses = createAsyncThunk(
  'performance/fetchCourseSentimentAnalyses',
  (courseID: string) =>
    axios
      .get<CourseSentimentAnalysis[]>(
        `/api/content-pipeline/performance-course/sentiment-analysis/course/${courseID}`,
      )
      .then(pluckData),
)

export let createCourseSentimentAnalysis = createAsyncThunk(
  'performance/createCourseSentimentAnalysis',
  (request: CourseSentimentAnalysisRequest) =>
    axios
      .post<{
        correlationID: string
      }>('/api/content-pipeline/performance-course/sentiment-analysis', request)
      .then(pluckData),
)

export let pollCourseSentimentAnalysisStatus = createAsyncThunk(
  'performance/pollCourseSentimentAnalysisStatus',
  (correlationID: string) =>
    axios
      .get<JobStatusResponse>(
        `/api/content-pipeline/performance-course/sentiment-analysis/${correlationID}`,
      )
      .then(pluckData),
)

export let fetchEventLikertAnalyses = createAsyncThunk(
  'performance/fetchEventLikertAnalyses',
  (eventID: string) =>
    axios
      .get<EventLikertAnalysis[]>(
        `/api/content-pipeline/performance-event/likert-analysis/event/${eventID}`,
      )
      .then(pluckData),
)

export let createLikertAnalysis = createAsyncThunk(
  'performance/createLikertAnalysis',
  (request: LikertAnalysisRequest) =>
    axios
      .post<{
        correlationID: string
      }>('/api/content-pipeline/performance-event/likert-analysis', request)
      .then(pluckData),
)

export let pollLikertAnalysisStatus = createAsyncThunk(
  'performance/pollLikertAnalysisStatus',
  (correlationID: string) =>
    axios
      .get<JobStatusResponse>(
        `/api/content-pipeline/performance-event/likert-analysis/${correlationID}`,
      )
      .then(pluckData),
)

export let fetchCourseLikertAnalyses = createAsyncThunk(
  'performance/fetchCourseLikertAnalyses',
  (courseID: string) =>
    axios
      .get<CourseLikertAnalysis[]>(
        `/api/content-pipeline/performance-course/likert-analysis/course/${courseID}`,
      )
      .then(pluckData),
)

export let createCourseLikertAnalysis = createAsyncThunk(
  'performance/createCourseLikertAnalysis',
  (request: CourseLikertAnalysisRequest) =>
    axios
      .post<{
        correlationID: string
      }>('/api/content-pipeline/performance-course/likert-analysis', request)
      .then(pluckData),
)

export let pollCourseLikertAnalysisStatus = createAsyncThunk(
  'performance/pollCourseLikertAnalysisStatus',
  (correlationID: string) =>
    axios
      .get<JobStatusResponse>(
        `/api/content-pipeline/performance-course/likert-analysis/${correlationID}`,
      )
      .then(pluckData),
)

export let fetchCourses = createAsyncThunk(
  'performance/fetchCoursesStatus',
  () =>
    axios
      .get<CourseWithSection[]>('/api/pipeline/courses')
      .then(pluckData)
      .then((data) =>
        data.map((course) => ({
          ...course,
          section: course.sectionName,
        })),
      ),
)

export let fetchEvents = createAsyncThunk('performance/fetchEventsStatus', () =>
  axios
    .get<any[]>('/api/pipeline/events')
    .then(pluckData)
    .then((data) =>
      data.map((event): Event => {
        let [eventType, location] = Object.entries<any>(event.eventType)[0]
        let flattenedLocation =
          eventType === 'hybrid' &&
          Array.isArray(location) &&
          location.length === 2
            ? {
                ...location[1],
                ...location[0],
                virtualName: location[1].name,
              }
            : location
        return {
          ...event,
          attendanceTrackingMechanism: {
            kind: isNotNil(event.attendanceTrackingMechanism.quizPasscode)
              ? 'quizPasscode'
              : 'managerTracked',
            quizPasscode: event.attendanceTrackingMechanism.quizPasscode,
          },
          category: {
            kind: event.category.other ? 'other' : event.category,
            other: event.category.other,
          },
          categoryName: isNotNil(event.category.other)
            ? 'Other'
            : {
                'airway|Respiratory|Ventilation':
                  'Airway|Respiratory|Ventilation',
                medical: 'Medical',
                trauma: 'Trauma',
                operations: 'Operations',
                'critical Care': 'Critical Care',
                cardiovascular: 'Cardiovascular',
                pediatrics: 'Pediatrics',
              }[event.category as string],
          location: {
            kind: eventType,
            ...flattenedLocation,
          },
          eventType:
            {
              virtual: 'Virtual',
              inPerson: 'In Person',
              hybrid: 'Hybrid',
            }[eventType] || 'Unknown',
        }
      }),
    ),
)

// ─── State type ───────────────────────────────────────────────────────────────

type PerformanceState = {
  nurseGrowth: NurseGrowth | null
  nurseGrowthStatus: Status
  learnerSnapshots: Record<string, LearnerSnapshot>
  learnerSnapshotsStatus: Record<string, Status>
  eventSentimentAnalyses: Record<string, EventSentimentAnalysis[]>
  courseSentimentAnalyses: Record<string, CourseSentimentAnalysis[]>
  sentimentAnalysisStatus: Record<string, Status>
  courseSentimentAnalysisStatus: Record<string, Status>
  activeSentimentPolling: {
    eventID?: string
    courseID?: string
    correlationID: string
  } | null
  sentimentPollingProgress: JobProgress[]
  eventLikertAnalyses: Record<string, EventLikertAnalysis[]>
  courseLikertAnalyses: Record<string, CourseLikertAnalysis[]>
  likertAnalysisStatus: Record<string, Status>
  courseLikertAnalysisStatus: Record<string, Status>
  activeLikertPolling: {
    eventID?: string
    courseID?: string
    correlationID: string
  } | null
  likertPollingProgress: JobProgress[]
  courses: CourseWithSection[]
  coursesStatus: Status
  events: Event[]
  eventsStatus: Status
}

export let fetchSpreadsheetData = createAsyncThunk(
  'performance/fetchSpreadsheetData',
  async ({ startDate, endDate }: { startDate?: string; endDate?: string }) => {
    let finalCSV = await axios
      .get<string>(
        `/api/pipeline/ancc-spreadsheet-data?startDate=${startDate}&endDate=${endDate}`,
      )
      .then(pluckData)

    let blob = new Blob([finalCSV], { type: 'text/csv' })
    let url = URL.createObjectURL(blob)
    let a = document.createElement('a')
    a.href = url
    a.download = 'course-data.csv'
    a.click()
    URL.revokeObjectURL(url)
  },
)

export let initialState: PerformanceState = {
  nurseGrowth: null,
  nurseGrowthStatus: 'uninitialized',
  learnerSnapshots: {},
  learnerSnapshotsStatus: {},
  eventSentimentAnalyses: {},
  courseSentimentAnalyses: {},
  sentimentAnalysisStatus: {},
  courseSentimentAnalysisStatus: {},
  activeSentimentPolling: null,
  sentimentPollingProgress: [],
  eventLikertAnalyses: {},
  courseLikertAnalyses: {},
  likertAnalysisStatus: {},
  courseLikertAnalysisStatus: {},
  activeLikertPolling: null,
  likertPollingProgress: [],
  courses: [],
  coursesStatus: 'uninitialized',
  events: [],
  eventsStatus: 'uninitialized',
}

// ─── Slice ────────────────────────────────────────────────────────────────────

export let performanceSlice = createSlice({
  name: 'performance',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Nurse Growth reducers
      .addCase(fetchNurseGrowth.pending, (state) => {
        state.nurseGrowthStatus = 'pending'
      })
      .addCase(fetchNurseGrowth.rejected, (state) => {
        state.nurseGrowthStatus = 'rejected'
      })
      .addCase(fetchNurseGrowth.fulfilled, (state, action) => {
        state.nurseGrowthStatus = 'fulfilled'
        state.nurseGrowth = action.payload
      })
      // Learner Snapshot reducers
      .addCase(fetchLearnerSnapshot.pending, (state, action) => {
        state.learnerSnapshotsStatus[learnerSnapshotCacheKey(action.meta.arg)] =
          'pending'
      })
      .addCase(fetchLearnerSnapshot.rejected, (state, action) => {
        state.learnerSnapshotsStatus[learnerSnapshotCacheKey(action.meta.arg)] =
          'rejected'
      })
      .addCase(fetchLearnerSnapshot.fulfilled, (state, action) => {
        let key = learnerSnapshotCacheKey(action.meta.arg)
        state.learnerSnapshotsStatus[key] = 'fulfilled'
        state.learnerSnapshots[key] = action.payload
      })
      // Sentiment Analysis reducers (event)
      .addCase(fetchEventSentimentAnalyses.pending, (state, action) => {
        state.sentimentAnalysisStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchEventSentimentAnalyses.rejected, (state, action) => {
        state.sentimentAnalysisStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchEventSentimentAnalyses.fulfilled, (state, action) => {
        state.sentimentAnalysisStatus[action.meta.arg] = 'fulfilled'
        // Preserve results that were stored from polling if the fetched data doesn't have them
        let existingAnalyses =
          state.eventSentimentAnalyses[action.meta.arg] || []
        let newAnalyses = action.payload.map((fetchedAnalysis) => {
          let existingAnalysis = existingAnalyses.find(
            (a) => a.correlationID === fetchedAnalysis.correlationID,
          )
          // If we have a result from polling but the fetched data doesn't, preserve it
          if (existingAnalysis?.result && !fetchedAnalysis.result) {
            return { ...fetchedAnalysis, result: existingAnalysis.result }
          }
          return fetchedAnalysis
        })
        state.eventSentimentAnalyses[action.meta.arg] = newAnalyses
      })
      .addCase(createSentimentAnalysis.fulfilled, (state, action) => {
        state.activeSentimentPolling = {
          eventID: action.meta.arg.eventID,
          correlationID: action.payload.correlationID,
        }
        state.sentimentPollingProgress = []
      })
      .addCase(pollSentimentAnalysisStatus.fulfilled, (state, action) => {
        state.sentimentPollingProgress = action.payload.progress

        // Store the result if we have one (works for both active polling and fetching old results)
        if (
          action.payload.status === 'SessionCompleted' &&
          action.payload.result
        ) {
          let resultResponse = action.payload.result.response
          // Find the analysis by correlationID across all events
          Object.keys(state.eventSentimentAnalyses).forEach((eventID) => {
            let analyses = state.eventSentimentAnalyses[eventID]
            if (analyses) {
              let analysisIndex = analyses.findIndex(
                (a) => a.correlationID === action.meta.arg,
              )
              if (analysisIndex !== -1) {
                analyses[analysisIndex].result = resultResponse
              }
            }
          })
        }

        // Clear active polling if this was an active polling request
        if (
          (action.payload.status === 'SessionCompleted' ||
            action.payload.status === 'SessionFailed') &&
          state.activeSentimentPolling?.correlationID === action.meta.arg
        ) {
          state.activeSentimentPolling = null
        }
      })
      // Course Sentiment Analysis reducers
      .addCase(fetchCourseSentimentAnalyses.pending, (state, action) => {
        state.courseSentimentAnalysisStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseSentimentAnalyses.rejected, (state, action) => {
        state.courseSentimentAnalysisStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseSentimentAnalyses.fulfilled, (state, action) => {
        state.courseSentimentAnalysisStatus[action.meta.arg] = 'fulfilled'
        // Preserve results that were stored from polling if the fetched data doesn't have them
        let existingAnalyses =
          state.courseSentimentAnalyses[action.meta.arg] || []
        let newAnalyses = action.payload.map((fetchedAnalysis) => {
          let existingAnalysis = existingAnalyses.find(
            (a) => a.correlationID === fetchedAnalysis.correlationID,
          )
          // If we have a result from polling but the fetched data doesn't, preserve it
          if (existingAnalysis?.result && !fetchedAnalysis.result) {
            return { ...fetchedAnalysis, result: existingAnalysis.result }
          }
          return fetchedAnalysis
        })
        state.courseSentimentAnalyses[action.meta.arg] = newAnalyses
      })
      .addCase(createCourseSentimentAnalysis.fulfilled, (state, action) => {
        state.activeSentimentPolling = {
          courseID: action.meta.arg.courseID,
          correlationID: action.payload.correlationID,
        }
        state.sentimentPollingProgress = []
      })
      .addCase(pollCourseSentimentAnalysisStatus.fulfilled, (state, action) => {
        state.sentimentPollingProgress = action.payload.progress

        // Store the result if we have one (works for both active polling and fetching old results)
        if (
          action.payload.status === 'SessionCompleted' &&
          action.payload.result
        ) {
          let resultResponse = action.payload.result.response
          // Find the analysis by correlationID across all courses
          Object.keys(state.courseSentimentAnalyses).forEach((courseID) => {
            let analyses = state.courseSentimentAnalyses[courseID]
            if (analyses) {
              let analysisIndex = analyses.findIndex(
                (a) => a.correlationID === action.meta.arg,
              )
              if (analysisIndex !== -1) {
                analyses[analysisIndex].result = resultResponse
              }
            }
          })
        }

        // Clear active polling if this was an active polling request
        if (
          (action.payload.status === 'SessionCompleted' ||
            action.payload.status === 'SessionFailed') &&
          state.activeSentimentPolling?.correlationID === action.meta.arg
        ) {
          state.activeSentimentPolling = null
        }
      })
      // Likert Analysis reducers (event)
      .addCase(fetchEventLikertAnalyses.pending, (state, action) => {
        state.likertAnalysisStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchEventLikertAnalyses.rejected, (state, action) => {
        state.likertAnalysisStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchEventLikertAnalyses.fulfilled, (state, action) => {
        state.likertAnalysisStatus[action.meta.arg] = 'fulfilled'
        let existingAnalyses = state.eventLikertAnalyses[action.meta.arg] || []
        let newAnalyses = action.payload.map((fetched) => {
          let existing = existingAnalyses.find(
            (a) => a.correlationID === fetched.correlationID,
          )
          if (existing?.result && !fetched.result) {
            return { ...fetched, result: existing.result }
          }
          return fetched
        })
        state.eventLikertAnalyses[action.meta.arg] = newAnalyses
      })
      .addCase(createLikertAnalysis.fulfilled, (state, action) => {
        state.activeLikertPolling = {
          eventID: action.meta.arg.eventID,
          correlationID: action.payload.correlationID,
        }
        state.likertPollingProgress = []
      })
      .addCase(pollLikertAnalysisStatus.fulfilled, (state, action) => {
        state.likertPollingProgress = action.payload.progress
        if (
          action.payload.status === 'SessionCompleted' &&
          action.payload.result
        ) {
          let resultResponse = action.payload.result.response
          Object.keys(state.eventLikertAnalyses).forEach((eventID) => {
            let analyses = state.eventLikertAnalyses[eventID]
            if (analyses) {
              let idx = analyses.findIndex(
                (a) => a.correlationID === action.meta.arg,
              )
              if (idx !== -1) {
                analyses[idx].result = resultResponse
              }
            }
          })
        }
        if (
          (action.payload.status === 'SessionCompleted' ||
            action.payload.status === 'SessionFailed') &&
          state.activeLikertPolling?.correlationID === action.meta.arg
        ) {
          state.activeLikertPolling = null
        }
      })
      // Likert Analysis reducers (course)
      .addCase(fetchCourseLikertAnalyses.pending, (state, action) => {
        state.courseLikertAnalysisStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseLikertAnalyses.rejected, (state, action) => {
        state.courseLikertAnalysisStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseLikertAnalyses.fulfilled, (state, action) => {
        state.courseLikertAnalysisStatus[action.meta.arg] = 'fulfilled'
        let existingAnalyses = state.courseLikertAnalyses[action.meta.arg] || []
        let newAnalyses = action.payload.map((fetched) => {
          let existing = existingAnalyses.find(
            (a) => a.correlationID === fetched.correlationID,
          )
          if (existing?.result && !fetched.result) {
            return { ...fetched, result: existing.result }
          }
          return fetched
        })
        state.courseLikertAnalyses[action.meta.arg] = newAnalyses
      })
      .addCase(createCourseLikertAnalysis.fulfilled, (state, action) => {
        state.activeLikertPolling = {
          courseID: action.meta.arg.courseID,
          correlationID: action.payload.correlationID,
        }
        state.likertPollingProgress = []
      })
      .addCase(pollCourseLikertAnalysisStatus.fulfilled, (state, action) => {
        state.likertPollingProgress = action.payload.progress
        if (
          action.payload.status === 'SessionCompleted' &&
          action.payload.result
        ) {
          let resultResponse = action.payload.result.response
          Object.keys(state.courseLikertAnalyses).forEach((courseID) => {
            let analyses = state.courseLikertAnalyses[courseID]
            if (analyses) {
              let idx = analyses.findIndex(
                (a) => a.correlationID === action.meta.arg,
              )
              if (idx !== -1) {
                analyses[idx].result = resultResponse
              }
            }
          })
        }
        if (
          (action.payload.status === 'SessionCompleted' ||
            action.payload.status === 'SessionFailed') &&
          state.activeLikertPolling?.correlationID === action.meta.arg
        ) {
          state.activeLikertPolling = null
        }
      })
      // Courses reducers
      .addCase(fetchCourses.pending, (state) => {
        state.coursesStatus = 'pending'
      })
      .addCase(fetchCourses.rejected, (state) => {
        state.coursesStatus = 'rejected'
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.coursesStatus = 'fulfilled'
        state.courses = action.payload
      })
      // Events reducers
      .addCase(fetchEvents.pending, (state) => {
        state.eventsStatus = 'pending'
      })
      .addCase(fetchEvents.rejected, (state) => {
        state.eventsStatus = 'rejected'
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.eventsStatus = 'fulfilled'
        state.events = action.payload
      })
  },
})

// ─── Selectors ────────────────────────────────────────────────────────────────

export let selectNurseGrowth = (state: AppRootState) =>
  state.performance.nurseGrowth
export let selectNurseGrowthStatus = (state: AppRootState) =>
  state.performance.nurseGrowthStatus

export let selectLearnerSnapshot =
  (args: LearnerSnapshotArgs) => (state: AppRootState) =>
    state.performance.learnerSnapshots[learnerSnapshotCacheKey(args)]
export let selectLearnerSnapshotStatus =
  (args: LearnerSnapshotArgs) => (state: AppRootState) =>
    state.performance.learnerSnapshotsStatus[learnerSnapshotCacheKey(args)]

export let selectEventSentimentAnalyses = (state: AppRootState) =>
  state.performance.eventSentimentAnalyses
export let selectSentimentAnalysisStatus = (state: AppRootState) =>
  state.performance.sentimentAnalysisStatus
export let selectEventSentimentAnalysesByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.performance.eventSentimentAnalyses[eventID]
export let selectSentimentAnalysisStatusByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.performance.sentimentAnalysisStatus[eventID]
export let selectActiveSentimentPolling = (state: AppRootState) =>
  state.performance.activeSentimentPolling
export let selectSentimentPollingProgress = (state: AppRootState) =>
  state.performance.sentimentPollingProgress

export let selectCourseSentimentAnalyses = (state: AppRootState) =>
  state.performance.courseSentimentAnalyses
export let selectCourseSentimentAnalysisStatus = (state: AppRootState) =>
  state.performance.courseSentimentAnalysisStatus
export let selectCourseSentimentAnalysesByCourseID =
  (courseID: string) => (state: AppRootState) =>
    state.performance.courseSentimentAnalyses[courseID]
export let selectCourseSentimentAnalysisStatusByCourseID =
  (courseID: string) => (state: AppRootState) =>
    state.performance.courseSentimentAnalysisStatus[courseID]

export let selectEventLikertAnalysesByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.performance.eventLikertAnalyses[eventID]
export let selectLikertAnalysisStatusByEventID =
  (eventID: string) => (state: AppRootState) =>
    state.performance.likertAnalysisStatus[eventID]
export let selectActiveLikertPolling = (state: AppRootState) =>
  state.performance.activeLikertPolling
export let selectLikertPollingProgress = (state: AppRootState) =>
  state.performance.likertPollingProgress

export let selectCourseLikertAnalysesByCourseID =
  (courseID: string) => (state: AppRootState) =>
    state.performance.courseLikertAnalyses[courseID]
export let selectCourseLikertAnalysisStatusByCourseID =
  (courseID: string) => (state: AppRootState) =>
    state.performance.courseLikertAnalysisStatus[courseID]

export let selectCourses = (state: AppRootState) => state.performance.courses
export let selectCoursesStatus = (state: AppRootState) =>
  state.performance.coursesStatus

export let selectEvents = (state: AppRootState) => state.performance.events
export let selectEventsStatus = (state: AppRootState) =>
  state.performance.eventsStatus

export default performanceSlice.reducer
